// Game Rating Service - Players can rate games they've played
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { updateGameSection } from './gameSectionService';

// Rate a game (1-5 stars)
// Each player can rate a game once per competition instance
export const rateGame = async (gameId, playerId, rating, gameSectionId = null) => {
  if (rating < 1 || rating > 5) {
    throw new Error('Rating must be between 1 and 5');
  }

  const ratingId = `${gameId}_${playerId}`;
  const ratingRef = doc(db, 'gameRatings', ratingId);

  // Check if rating already exists
  const existingRating = await getDoc(ratingRef);
  const oldRating = existingRating.exists() ? existingRating.data().rating : null;

  const ratingData = {
    gameId,
    playerId,
    gameSectionId, // Link to library game (if applicable)
    rating,
    updatedAt: serverTimestamp()
  };

  if (!existingRating.exists()) {
    ratingData.createdAt = serverTimestamp();
  }

  await setDoc(ratingRef, ratingData, { merge: true });

  // Update game section average rating if linked to library
  if (gameSectionId) {
    await updateGameSectionRating(gameSectionId, oldRating, rating);
  }

  return { id: ratingId, ...ratingData };
};

// Get a player's rating for a specific game
export const getPlayerRatingForGame = async (gameId, playerId) => {
  const ratingId = `${gameId}_${playerId}`;
  const ratingRef = doc(db, 'gameRatings', ratingId);
  const ratingSnap = await getDoc(ratingRef);

  if (!ratingSnap.exists()) {
    return null;
  }

  return {
    id: ratingSnap.id,
    ...ratingSnap.data()
  };
};

// Get all ratings for a game
export const getRatingsForGame = async (gameId) => {
  const ratingsRef = collection(db, 'gameRatings');
  const q = query(ratingsRef, where('gameId', '==', gameId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Calculate average rating for a game
export const getAverageRatingForGame = async (gameId) => {
  const ratings = await getRatingsForGame(gameId);

  if (ratings.length === 0) {
    return { average: 0, count: 0 };
  }

  const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
  return {
    average: Math.round((sum / ratings.length) * 10) / 10, // Round to 1 decimal
    count: ratings.length
  };
};

// Get all ratings for a game section (library game) across all competitions
export const getRatingsForGameSection = async (gameSectionId) => {
  const ratingsRef = collection(db, 'gameRatings');
  const q = query(ratingsRef, where('gameSectionId', '==', gameSectionId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Calculate average rating for a game section (library game)
export const getAverageRatingForGameSection = async (gameSectionId) => {
  const ratings = await getRatingsForGameSection(gameSectionId);

  if (ratings.length === 0) {
    return { average: 0, count: 0 };
  }

  const sum = ratings.reduce((acc, r) => acc + r.rating, 0);
  return {
    average: Math.round((sum / ratings.length) * 10) / 10,
    count: ratings.length
  };
};

// Update game section rating when a rating is added/changed
// This recalculates the average for the library game
const updateGameSectionRating = async (gameSectionId, oldRating, newRating) => {
  try {
    const { average, count } = await getAverageRatingForGameSection(gameSectionId);

    await updateGameSection(gameSectionId, {
      averageRating: average,
      totalRatings: count
    });
  } catch (err) {
    console.error('Error updating game section rating:', err);
    // Don't throw - this is a non-critical update
  }
};

// Delete a player's rating for a game
export const deleteRating = async (gameId, playerId, gameSectionId = null) => {
  const ratingId = `${gameId}_${playerId}`;
  const ratingRef = doc(db, 'gameRatings', ratingId);

  await deleteDoc(ratingRef);

  // Update game section average if linked
  if (gameSectionId) {
    await updateGameSectionRating(gameSectionId, null, null);
  }
};

// Get all ratings by a player
export const getRatingsByPlayer = async (playerId) => {
  const ratingsRef = collection(db, 'gameRatings');
  const q = query(ratingsRef, where('playerId', '==', playerId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};
